<template>
  <b-card>
    <slot name="hint" />
    <div class="d-flex justify-content-between align-items-center mb-0">
      <b-card-title class="mb-0">
        Outcome Scatter
      </b-card-title>
      <div
        v-if="$slots['header-hint'] || $scopedSlots['header-hint'] || (!isHomepage && portfolioId !== 'u')"
        class="d-flex align-items-center flex-shrink-0 ml-1"
      >
        <slot name="header-hint" />
        <b-button
          v-if="!isHomepage && portfolioId !== 'u'"
          variant="outline-secondary"
          size="sm"
          class="d-flex align-items-center"
          @click="downloadCsv"
        >
          <b-img
            :src="require('@/assets/images/icons/file-icons/csv.png')"
            height="20"
            width="20"
          />
          <span class="ml-50 font-weight-bold text-body">Download CSV</span>
        </b-button>
      </div>
    </div>
    <div class="font-small-3 mb-1 text-muted">
      Product outcomes shown for matured products and live products using latest valuations
    </div>
    <b-card-text>
      <div v-if="loading">
        <loading-container />
      </div>
      <div
        v-else
        class="position-relative"
      >
        <div
          v-if="noData"
          class="no-data-overlay"
        >
          <span class="text-muted">No data available</span>
        </div>
        <app-echart-scatter
          ref="chart"
          :key="chartKey"
          :option-data="chartData"
        />
      </div>
    </b-card-text>
  </b-card>
</template>

<script>
import {
  BCard, BCardText, BCardTitle, BButton, BImg,
} from 'bootstrap-vue'
import AppEchartScatter from '@core/components/charts/echart/AppEchartScatter.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import renderChartToBase64 from '../../breakdown/charts/offscreenRender'

export default {
  components: {
    BCard,
    BCardText,
    BCardTitle,
    BButton,
    BImg,
    AppEchartScatter,
    LoadingContainer,
  },
  props: {
    payoffScatter: {
      type: Object,
      default: null,
    },
    yearOnly: {
      type: Boolean,
      default: false,
    },
    isHomepage: {
      type: Boolean,
      default: false,
    },
    portfolioName: {
      type: String,
      default: '',
    },
    portfolioId: {
      type: [String, Number],
      default: null,
    },
    searchFilter: {
      type: Object,
      default: null,
    },
    productWeightMode: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  data() {
    return {
      cachedImage: null,
      noData: false,
      chartKey: 0,
      chartData: {
        legend: {},
        grid: {},
        xAxis: {},
        yAxis: {},
        series: [],
      },
    }
  },
  computed: {
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
  },
  watch: {
    payoffScatter: {
      handler(data) {
        this.updateChart(data)
      },
      immediate: true,
    },
    skin() {
      // Re-process data when theme changes
      this.updateChart(this.payoffScatter)
    },
    loading(isLoading) {
      if (!isLoading) this.scheduleChartClickBinding()
    },
    chartData: {
      handler(option) {
        if (option && option.series && option.series.length > 0) {
          this.cachedImage = renderChartToBase64(option)
        }
      },
      deep: true,
    },
  },
  mounted() {
    window.addEventListener('resize', this.resizeChart)
    this.scheduleChartClickBinding()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
  },
  methods: {
    downloadCsv() {
      this.$emit('download-csv')

      const isUniverse = this.portfolioId === 'u'
      const isVirtual = this.portfolioId === 'v'
      let method
      let params
      if (isUniverse) {
        method = 'downloadCSVUniverseScatter'
        params = {}
      } else if (isVirtual) {
        method = 'downloadCSVVirtualPortfolioScatter'
        params = {
          searchfilter: JSON.stringify(this.searchFilter),
          productweightingmode: this.productWeightMode || 'equal',
        }
      } else {
        method = 'downloadCSVScatter'
        params = {
          portfolioid: this.portfolioId,
          portfolioname: this.portfolioName,
        }
      }
      this.$http.get(`lifecycle/public/report.cfc?method=${method}`, { params, responseType: 'blob' })
        .then(response => {
          const url = window.URL.createObjectURL(response.data)
          const a = document.createElement('a')
          a.style.display = 'none'
          a.href = url
          // eslint-disable-next-line prefer-destructuring
          a.download = response.headers['content-disposition'].match(/"([^']+)"/)[1]
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
        })
    },
    getDataURL() {
      return this.cachedImage || null
    },
    updateChart(data) {
      if (!this.isValidScatterData(data)) {
        this.setNoData()
        return
      }

      try {
        this.processPayoffScatterData(data)
      } catch (err) {
        console.error('Error processing payoff scatter chart:', err)
        this.setNoData()
      }
    },
    isValidScatterData(data) {
      return !!data && !!data.options && Array.isArray(data.data)
    },
    setNoData() {
      this.noData = true
      this.cachedImage = null
      this.chartData = {
        legend: { show: false },
        grid: {},
        xAxis: {},
        yAxis: {},
        series: [],
      }
      this.chartKey += 1
    },
    processPayoffScatterData(chartData) {
      const today = new Date().getTime()
      const xMin = new Date(chartData.options.xmin).getTime()
      const xMax = new Date(chartData.options.xmax).getTime()
      const yMin = chartData.options.ymin
      const yMax = chartData.options.ymax

      if (Number.isNaN(xMin) || Number.isNaN(xMax) || yMin === undefined || yMax === undefined) {
        this.setNoData()
        return
      }

      // Check if there's actual data (scatter points with data)
      const dataSeries = chartData.data.filter(Boolean)
      const scatterSeries = dataSeries.filter(series => series.name !== 'XYLine')
      const hasData = scatterSeries.some(s => (
        Array.isArray(s.data) && s.data.some(point => Array.isArray(point) && point.length >= 2)
      ))
      this.noData = !hasData

      const colors = ['#1a73e8', '#28c76f', '#ea5455', '#82868b', '#00cfe8', '#ff9f43']

      // Parse date strings from backend which may use long format: "September, 27 2019 00:00:00"
      const parseDate = str => {
        const ts = Date.parse(str)
        if (!Number.isNaN(ts)) return ts
        // Fallback: strip the comma and re-parse "September 27 2019 00:00:00"
        return typeof str === 'string' ? Date.parse(str.replace(',', '')) : NaN
      }

      // Format helper based on yearOnly prop
      const formatDate = value => {
        const d = new Date(value)
        if (this.yearOnly) return `${d.getFullYear()}`
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        return `${d.getFullYear()}-${mm}`
      }

      // Determine if deduplication would suppress all labels (range < 1 month apart per tick)
      const splitNum = this.yearOnly ? Math.max(2, Math.round((xMax - xMin) / (365.25 * 24 * 60 * 60 * 1000) / 5)) : 5
      const simulatedTicks = Array.from({ length: splitNum + 1 }, (_, i) => xMin + ((xMax - xMin) * i) / splitNum)
      const uniqueLabels = new Set(simulatedTicks.map(formatDate))
      const allDuplicates = uniqueLabels.size === 1
      const showEdgeLabels = allDuplicates

      // Convert ApexCharts scatter format to ECharts format
      // Filter out 'XYLine' series (helper line that shouldn't be displayed)
      const echartsSeries = dataSeries
        .filter(series => series.name !== 'XYLine')
        .map((series, index) => {
          const productSeries = (chartData.products || []).find(p => p.name === series.name)
          return {
            name: series.name,
            type: 'scatter',
            data: (series.data || [])
              .filter(point => Array.isArray(point) && point.length >= 2)
              .map((point, i) => [parseDate(point[0]), point[1], productSeries?.datalabel?.[i] ?? '', productSeries?.data?.[i] ?? null]),
            symbolSize: 4,
            cursor: 'pointer',
            itemStyle: {
              color: colors[index % colors.length],
            },
          }
        })

      // Red horizontal line at y = 100
      const referenceSeries = [
        {
          type: 'line',
          data: [[xMin, 100], [xMax, 100]],
          lineStyle: {
            color: '#ea5455',
            width: 2,
          },
          symbol: 'none',
          silent: true,
          legendHoverLink: false,
        },
        // Red vertical line at as-of date
        {
          type: 'line',
          data: [[today, yMin], [today, yMax]],
          lineStyle: {
            color: '#ea5455',
            width: 2,
          },
          symbol: 'none',
          silent: true,
          legendHoverLink: false,
        },
      ]

      this.chartData = {
        tooltip: {
          trigger: 'item',
          axisPointer: {
            type: 'cross',
          },
          formatter: p => {
            if (!p || p.seriesType !== 'scatter') return ''
            const label = p.data[2] ? `${p.data[2]}<br/>` : ''
            return `${label}${p.marker} ${p.seriesName}: ${p.value[1]}`
          },
        },
        legend: {
          show: true,
          left: 0,
        },
        grid: {
          left: 70,
          right: 30,
          bottom: 70,
          top: 60,
          containLabel: false,
        },
        xAxis: {
          type: 'time',
          name: 'Maturity',
          nameLocation: 'middle',
          nameGap: 35,
          min: xMin,
          max: xMax,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          splitNumber: this.yearOnly ? Math.max(2, Math.round((xMax - xMin) / (365.25 * 24 * 60 * 60 * 1000) / 5)) : 5,
          axisLabel: {
            fontSize: 12,
            showMinLabel: showEdgeLabels,
            showMaxLabel: showEdgeLabels,
            formatter: (() => {
              let seen = {}
              let lastValue = -Infinity
              return value => {
                if (value <= lastValue) seen = {}
                lastValue = value
                const label = formatDate(value)
                if (!seen[label]) {
                  seen[label] = true
                  return label
                }
                return ''
              }
            })(),
          },
          scale: true,
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Payoff value',
          nameLocation: 'middle',
          nameGap: 50,
          min: yMin,
          max: yMax,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          scale: true,
          splitLine: {
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
        },
        series: [...echartsSeries, ...referenceSeries],
      }
      // Increment key to force chart re-render
      this.chartKey += 1
      this.scheduleChartClickBinding()
    },
    scheduleChartClickBinding() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.resizeChart()
          this.bindChartClick()
        }, 100)
      })
    },
    bindChartClick() {
      const chartInstance = this.$refs.chart && this.$refs.chart.getChartInstance && this.$refs.chart.getChartInstance()
      if (chartInstance) {
        chartInstance.off('click')
        chartInstance.on('click', params => {
          if (params.seriesType === 'scatter' && params.data) {
            const reportNumber = params.data[3]
            if (reportNumber) {
              this.$router.push({ name: 'product-report', params: { reportNumber } })
            }
          }
        })
      }
    },
    resizeChart() {
      if (this.$refs.chart) {
        this.$refs.chart.resize()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.no-data-overlay {
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  font-size: 1.1rem;
}
</style>
